import { Button, addToast } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Col, Row } from 'reactstrap';

import { HTTP_STATUS_CODE } from '../../../../api/request';
import { createPetition } from '../../../../api/requests/petitions';
import { useUserState } from '../../../../hooks/useUserState';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  CreditPeriod,
  SelectedSection,
} from '../../../../types/courseSelectionTypes';
import { PetitionTypes } from '../../../../types/petitions';

interface SubmitReliquidationProps {
  selectedSections: SelectedSection[];
  creditsPeriod: CreditPeriod;
  studyPlanEnrollmentId: number;
  periodId?: number;
}

export default function SubmitReliquidation({
  selectedSections,
  creditsPeriod,
  studyPlanEnrollmentId,
  periodId,
}: SubmitReliquidationProps) {
  const { t } = useTranslation(TranslationsKeys.PETITIONS);
  const history = useHistory();
  const { userData } = useUserState();
  const [loading, setLoading] = useState(false);

  const text = useMemo(() => {
    const prefix = 'creditReliquidation.submit';
    return {
      button: t(`${prefix}.button`),
      success: t(`${prefix}.success`),
      error: t(`${prefix}.error`),
      validation: {
        noSections: t(`${prefix}.validation.noSections`),
        minCredits: t(`${prefix}.validation.minCredits`),
        sameCredits: t(`${prefix}.validation.sameCredits`),
        noPeriod: t(`${prefix}.validation.noPeriod`),
      },
    };
  }, [t]);

  const selectedCredits = useMemo(() => {
    return selectedSections.reduce(
      (sum, section) => sum + (section.course?.credits ?? 0),
      0,
    );
  }, [selectedSections]);

  const validation = useMemo(() => {
    if (selectedSections.length === 0) {
      return { valid: false, message: text.validation.noSections };
    }
    if (selectedCredits < creditsPeriod.creditMin) {
      return { valid: false, message: text.validation.minCredits };
    }
    if (
      creditsPeriod.creditsContracted !== null &&
      selectedCredits === creditsPeriod.creditsContracted
    ) {
      return { valid: false, message: text.validation.sameCredits };
    }
    if (!periodId) {
      return { valid: false, message: text.validation.noPeriod };
    }
    return { valid: true, message: null };
  }, [selectedSections, selectedCredits, creditsPeriod, periodId, text]);

  const handleSubmit = useCallback(async () => {
    if (!validation.valid || !periodId) {
      if (validation.message) {
        addToast({
          icon: 'warning',
          color: 'warning',
          text: validation.message,
        });
      }
      return;
    }

    setLoading(true);

    const petitionData = {
      studyPlanEnrollmentId,
      phone: userData?.phone ?? 'N/A',
      email: userData?.email ?? '',
      typeId: PetitionTypes.CREDIT_RELIQUIDATION,
      title: `Reliquidación de créditos`,
      description: `Solicitud de reliquidación de ${
        creditsPeriod.creditsContracted ?? 0
      } a ${selectedCredits} créditos`,
      docIds: [],
      creditReliquidationData: {
        periodId,
        recommendedCredits: creditsPeriod.creditsContracted ?? 0,
        creditsToEnroll: selectedCredits,
      },
    };

    const { status, error } = await createPetition(petitionData);

    if (status === HTTP_STATUS_CODE.CREATED) {
      addToast({
        icon: 'success',
        color: 'success',
        text: text.success,
      });
      history.push('/petitions/current');
    } else if (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: text.error,
      });
    }

    setLoading(false);
  }, [
    validation,
    studyPlanEnrollmentId,
    periodId,
    userData,
    creditsPeriod,
    selectedCredits,
    history,
    text,
  ]);

  return (
    <Row className="mt-4">
      <Col md={12} className="d-flex justify-content-end">
        <Button
          text={text.button}
          color="primary"
          size="lg"
          loading={loading}
          disabled={!validation.valid}
          onClick={handleSubmit}
        />
      </Col>
    </Row>
  );
}
